'use client';

import { useEffect, useRef, useState } from 'react';

const COMPLETION_LINGER_MS = 4000;

/**
 * Fires a polite live-region announcement when a file transitions from processing → done.
 * Triggers on the falling edge of `isProcessing` (announcement only fires on the falling edge).
 * Self-clears after `COMPLETION_LINGER_MS` so the live region doesn't linger in the a11y tree.
 * Clears immediately if `isProcessing` returns to true before the linger window ends.
 * Pass `isProcessing || !!error` to suppress the announcement on terminal failure transitions.
 */
export function useUploadCompletionMessage(isProcessing: boolean, message: string): string | null {
  const prevProcessingRef = useRef(isProcessing);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [announcement, setAnnouncement] = useState<string | null>(null);

  useEffect(() => {
    const wasProcessing = prevProcessingRef.current;
    prevProcessingRef.current = isProcessing;
    if (isProcessing) {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setAnnouncement(null);
      return;
    }
    if (!wasProcessing) return;
    setAnnouncement(message);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setAnnouncement(null);
    }, COMPLETION_LINGER_MS);
  }, [isProcessing, message]);

  // Clean up on unmount
  useEffect(
    () => () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    },
    [],
  );

  return announcement;
}
